import { PageWithNavigation } from '@/src/components';
import React, { cloneElement, useEffect, useMemo } from 'react';
import ChargeAnytimePlans from '@/src/pages/Offers/components/cards/ChargeAnytimePlans/ChargeAnytimePlans';
import {
  useChargeAnytimeContext,
  Features,
  PlanDetails,
  useChargeAnytimeProducts,
  EV,
} from '@monovo/ev-anytime';
import { ROUTE_ANYTIME_ELIGIBILITY } from '@/src/constants/routes';
import { useHistory } from 'react-router-dom-v5';
import { Button, P, Stack, theme } from '@ovotech/element-v5';
import {
  CancellingPlan,
  ClearWattEvBatteryCheck,
  EvChargerCover,
  KwikFitClubDiscount,
  MultipleCars,
  PayAsYouGo,
  PayMonthly,
  Perks,
  PublicCharging,
  VolvoPayAsYouGo,
  PartnerPlanDetails,
} from '@/src/pages/Offers/components/cards/ChargeAnytimePlans/Details/FullDetails';
import { ButtonContainer, ContentContainer } from './SelectedPlan.styled';
import { Loading } from '@/src/pages/AnytimeEligibilityV2/components/Loading';
import { TechnicalGlitchPage } from '@/src/pages/AnytimeEligibilityV2/pages/TechnicalGlitchPage/TechnicalGlitchPage';
import { PageHeaderV2 } from '../../components/PageHeaderV2';
import { ProductType } from '../../components/PlanSelector';
import { useAnalytics } from '@ovotech/ui-tools';

const analyticsEventProperties = {
  Screen: EV.SCREENS.AVAILABLE_ADD_ONS,
  journeyName: 'Charge Anytime',
};

const getPlanDetails = (planDetails: PlanDetails, features: Features) => {
  const details = [];
  const hasMultipleEvs = 'multipleEvs' in features;
  const hasKwikFit = 'promotion' in features;
  const hasBatteryCover = 'batteryHealthCheck' in features;

  if (planDetails.productType === 'payg') {
    return [<PayAsYouGo planDetails={planDetails} key="payg" />];
  }

  if (planDetails.id === 'payg-volvo') {
    return [<VolvoPayAsYouGo planDetails={planDetails} key="payg-volvo" />];
  }

  if (planDetails.id === 'paym-oem-tier-one') {
    return [<PartnerPlanDetails key="oem" />];
  }

  details.push(<PayMonthly key="monthly" />);

  if (hasMultipleEvs) {
    details.push(<MultipleCars key="multi-car" />);
  }

  details.push(<CancellingPlan key="cancel-plan" />);

  details.push(<Perks key="perks" />);

  const credit = hasMultipleEvs ? '240' : '120';
  const publicChargingElement = PublicCharging(credit);
  details.push(cloneElement(publicChargingElement, { key: 'public' }));

  if (hasKwikFit) {
    details.push(<KwikFitClubDiscount key="kwik-fit" />);
  }

  if (hasBatteryCover) {
    details.push(<ClearWattEvBatteryCheck key="battery-cover" />);
  }

  details.push(<EvChargerCover key="charger-cover" />);

  return details;
};

export const SelectedPlan = () => {
  const history = useHistory();
  const { selectedPlanId, smartCharger, make } = useChargeAnytimeContext();
  const { dispatch: analyticsDispatch } = useAnalytics();

  const {
    data: products,
    isLoading,
    isError,
  } = useChargeAnytimeProducts('all');

  const allProducts = useMemo(() => (products ? products : []), [products]);

  const product = useMemo(() => {
    if (!selectedPlanId) {
      return null;
    }
    return allProducts.find(p => p.id === selectedPlanId) || null;
  }, [allProducts, selectedPlanId]);

  useEffect(() => {
    if (!selectedPlanId || !smartCharger || !make) {
      history.push(ROUTE_ANYTIME_ELIGIBILITY.SMART_CHARGER);
    }
  }, [selectedPlanId, history, smartCharger, make]);

  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0); // 0ms delay often pushes it after the current render cycle
    return () => clearTimeout(timer);
  }, [selectedPlanId]);

  if (!product || isLoading) {
    return <Loading />;
  }

  if (isError) {
    return <TechnicalGlitchPage header={'Check your set-up'} />;
  }

  const goToNextPage = () => {
    analyticsDispatch({
      type: 'click',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_SELECTED_MODAL_NEXT_CLICKED,
      properties: {
        ...analyticsEventProperties,
        planName: product.title,
      },
    });
    history.push(ROUTE_ANYTIME_ELIGIBILITY.CONSENT);
  };

  const onBackPress = () => {
    analyticsDispatch({
      type: 'click',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_SELECTED_MODAL_CLOSE_CLICKED,
      properties: {
        ...analyticsEventProperties,
        planName: product.title,
      },
    });
    history.goBack();
  };

  const fullDetailsArray = getPlanDetails(product, product.features);
  const showMultipleEvsBadge = 'multipleEvs' in product.features;

  const selectedPlanFootnoteContent = [
    {
      key: 'selected-plan-footnote-1',
      text: 'OVO is a trading name of OVO Energy Ltd, an appointed representative of OVO Home Services Ltd (for general insurance distribution) which is authorised and regulated by the Financial Conduct Authority under firm reference number 824122.',
    },
    {
      key: 'selected-plan-footnote-2',
      text: 'OVO Home Services Ltd and OVO Energy Ltd are part of the OVO Group Ltd.',
    },
  ];

  return (
    <PageWithNavigation title="EV Charge Anytime - Eligibility">
      <Stack spaceBetween={8}>
        <PageHeaderV2 title="You’ve selected" />
        <ContentContainer>
          <ChargeAnytimePlans
            plan={product}
            fullDetails={fullDetailsArray}
            showMultipleEvsBadge={showMultipleEvsBadge}
            variant={'selected-plan'}
          />
          {(product.productType === ProductType.MONTHLY ||
            product.id === 'paym-oem-tier-one') &&
            selectedPlanFootnoteContent.map(content => (
              <P
                key={`P-${content.key}`}
                style={{
                  alignSelf: 'stretch',
                  fontSize: theme.font.size.sm.mediaQuery.lg,
                  color: theme.color.surface.onSurfaceVariant,
                }}
              >
                {content.text}
              </P>
            ))}
        </ContentContainer>
        <ButtonContainer>
          <Button variant="outline" onClick={onBackPress}>
            Back
          </Button>
          <Button onClick={goToNextPage}>Next</Button>
        </ButtonContainer>
      </Stack>
    </PageWithNavigation>
  );
};
